import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { ApiKeyApiProvider, useApiKeyApi } from "../Services/ApiKeyApiProvider";
import { AlertProvider, useAlert } from "../Components/AlertContext";
import {ConfirmationProvider, useConfirmation} from "../Components/ConfirmationContext";
import Breadcrumbs from "../Components/Breadcrumbs";
import {CustomInput, CustomSelect} from "../Components/CustomFormElements";
import Button from "../Components/Buttons";
import PageLoader from "../Components/PageLoader";
import { isEmpty } from "../utils/validationUtil";
import PeriodSelector from "../Components/PeriodSelector";
import { CustomCodeEditor, CustomToggle } from '../Components/CustomFormElements';


function ApiKeyContent({ canEdit }) {
  const { organizationId, apiKeyId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const {showConfirmation} = useConfirmation();


  const { getApiKeyById, createApiKey, updateApiKey } = useApiKeyApi();

  const [name, setName] = useState("");
  const [api_key, setApiKey] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [customTo, setCustomTo] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("6_months");

  const [loading, setLoading] = useState(true);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const periods = [
    { label: "3 Years", value: "3_years" },
    { label: "1 Year", value: "1_year" },
    { label: "6 Months", value: "6_months" },
    { label: "1 Month", value: "1_month" },
    { label: "Custom", value: "custom" },
  ];

  const getDateRangeByPeriod = (period) => {
    const today = new Date();
    let expiresAt = null;

    switch (period) {
      case "3_years":
        expiresAt = new Date(today);
        expiresAt.setFullYear(today.getFullYear() + 3);
        break;
      case "1_year":
        expiresAt = new Date(today);
        expiresAt.setFullYear(today.getFullYear() + 1);
        break;
      case "6_months":
        expiresAt = new Date(today);
        expiresAt.setMonth(today.getMonth() + 6);
        break;
      case "1_month":
        expiresAt = new Date(today);
        expiresAt.setMonth(today.getMonth() + 1);
        break;
      default:
        expiresAt = null;
    }

    return {
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    };
  };

  useEffect(() => {
    const fetchApiKey = async () => {
      setLoading(true);
      try {
        console.log("Fetching API Key with ID:", apiKeyId);
        if (apiKeyId !== "new-api-key") {
          const response = await getApiKeyById(apiKeyId);
          console.log("Fetched API Key:", response);
          if (response) {
            setName(response.name || "");
            setExpiresAt(response.expires_at || "");
            setApiKey(response.api_key || "");
            setPermissions(response.permissions || []);
            setIsNew(false);
            setIsDisabled(!canEdit)
            setIsActive(response.is_active);
          }
        }
        else {
          setIsNew(true);
          setIsDisabled(!canEdit);
          setName("");
          setExpiresAt("");
          setPermissions([]);
        }
      } catch {
        showAlert("fail", "Failed to load API Key");
      } finally {
        setLoading(false);
      }
    };

    fetchApiKey();
  }, [apiKeyId, canEdit]);

  const onCancel = () => {
    navigate(-1);
  };

  const onSave = async () => {
    if (isEmpty(name)) {
      showAlert("fail", "Name is required");
      return;
    }

    let expiresAtValue;
    if (selectedPeriod === "custom") {
      expiresAtValue = customTo;
    } else {
      expiresAtValue = getDateRangeByPeriod(selectedPeriod).expiresAt;
    }

    const data = {
      name,
      permissions,
      expires_at: expiresAtValue
    };

    if (isNew) {
      showConfirmation(
        `Are you sure you want to create a new API Key?`,
        async () => {
          try {
            setLoading(true);
            const result = await createApiKey(data);
            console.log("API Key created successfully:", result);
            showAlert("success", "API Key successfully created!");
            navigate(`/organizations/${organizationId}/api-key/${result.id}`);
          } catch (error) {
            showAlert("fail", "Failed to create API Key");
          } finally {
            setLoading(false);
          }
        }
      )
    } else {
      showConfirmation(
        `Are you sure you want to save the changes?`,
        async () => {
          try {
            setLoading(true);
            data.is_active = isActive;
            await updateApiKey(apiKeyId, data);
            showAlert("success", "API Key successfully updated!");
          } catch (error) {
            showAlert("fail", "Failed to update channel");
          } finally {
            setLoading(false);
          }
        }
      )
    }
  };

  const handlePeriodChange = (e) => setSelectedPeriod(e.target.value);

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{isNew ? "Create API Key" : "Edit API Key"}</h2>

          <div className="input-block">
            <CustomInput
                id="api-key-name"
                label="Name"
                placeholder="Enter API Key Name"
                value={name}
                onChange={(id, val) => setName(val)}
                disabled={isDisabled}
            />
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                  id="permissions-select"
                  label="Permissions"
                  options={[
                    {value: "all:assistant", label: "Assistant"},
                    {value: "all:channel", label: "Channel"},
                  ]}
                  value={permissions}
                  multiSelect={true}
                  onChange={(items) => setPermissions(items)}
                  disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block">
            <label>Expires At</label>
            <PeriodSelector selectedPeriod={selectedPeriod} onChange={handlePeriodChange} periods={periods}/>
            {selectedPeriod === "custom" && (
                <div className="input-group mt-2">
                  <input
                      type="datetime-local"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                  />
                </div>
            )}
          </div>

          {!isNew && (
              <>
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Activate Key</div>
                    <div className="small-font">
                      Set the key to be active or inactive to enable or disable it
                    </div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomToggle
                      id="api-key-is-active"
                      label={isActive ? "Active" : "Inactive"}
                      checked={isActive}
                      onChange={(id, value) => setIsActive(value)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div className="input-block">
                  <h2 className='h2-title'>API Key</h2>
                  <CustomCodeEditor
                    id="api-key"
                    value={api_key}
                    boxtitle=''
                    wrapText={true}
                    language="javascript"
                    editable={false}
                    height='auto'
                    highlightActiveLine={false}
                    lightTheme='githubLight'
                    darkTheme='dracula'
                    lineNumbers={false}
                    foldGutter={false}
                  />
                </div>
              </>

          )}

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}

function ApiKey() {
  return (
      <AlertProvider>
        <ConfirmationProvider>
          <ApiKeyApiProvider>
            <ApiKeyContent canEdit={true}/>
          </ApiKeyApiProvider>
        </ConfirmationProvider>
      </AlertProvider>
  );
}

export default ApiKey;
